import {
  Avatar,
  Button,
  Divider,
  Grid,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { GetAppRounded } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import clsx from 'clsx';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { RootState } from '../../app/store';
import { ContentTypeLabel } from '../../common/enum/ContentTypeLabel';
import DocRevisionInfoCard from '../../components/DocRevisionInfoCard';
import ProjectInfoCard from '../../components/ProjectInfoCard';
import { useAppDispatch } from '../../hooks/useAppRudux';
import {
  getWatermark,
  SubmittedWatermark,
  Watermark
} from '../../localStorage/watermark';
import Breadcrumb from '../Breadcrumb';
import loadingSlice from '../Loading/loadingSlice';
import {
  getProjectDocumentVersionFileAsync,
  getProjectDocumentVersionFileOnlyAsync
} from './projectDocumentVersionFileSlice';
import {
  getColors,
  Position,
  getPositions,
  Rotation,
  rotations
} from '../../components/AdjustWatermark';

export default function DocumentVersionFileView() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const params = useParams<{ versionId: string }>();
  const versionId = Number.parseInt(params.versionId, 10);

  const { user } = useSelector((state: RootState) => state.userProfile);

  const { selectedProjectDocumentRevisionFile } = useSelector(
    (state: RootState) => state.projectDocumentRevisionFiles
  );
  const { selectedProjectDocumentRevision } = useSelector(
    (state: RootState) => state.projectDocumentRevisions
  );
  const { selectedProjectDocument } = useSelector(
    (state: RootState) => state.projectDocuments
  );
  const { selectedProject } = useSelector((state: RootState) => state.projects);

  const isPdfFile = Boolean(
    selectedProjectDocumentRevisionFile?.s3ContentType === ContentTypeLabel.pdf
  );

  const [watermarks, setWatermarks] = useState<Watermark[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    let watermarks = getWatermark();
    if (watermarks) {
      setWatermarks(
        watermarks.map((watermark: Watermark) => {
          let text = watermark.text;
          let position = watermark.position;
          let rotation = watermark.rotation;
          if (watermark.name === 'tradeSecretLabel') {
            text =
              selectedProjectDocument?.accessControl.name.toUpperCase() ||
              t('projectDocumentVersionFileView.labels.scgConfidential').toUpperCase();
          }
          if (watermark.name === 'actionByLabel') {
            text = `${t('projectDocumentVersionFileView.labels.downloadBy')} ${
              user ? `${user.firstName} ${user.lastName}` : t('projectDocumentVersionFileView.labels.backspaceUser')
            }, ${moment().format('DD-MMM-YYYY')}`;
          }
          if (
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.word
          ) {
            position = { id: 3, name: 'Center', value: 'Center' };
            rotation = { id: 2, name: '45', value: 45 };
          }
          if (
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.excel
          ) {
            position =
              watermark.name === 'tradeSecretLabel'
                ? { id: 1, name: 'Top-Left', value: 'TopLeft' }
                : { id: 2, name: 'Top-Right', value: 'TopRight' };
            rotation = { id: 1, name: '0', value: 0 };
          }
          return {
            ...watermark,
            text,
            position,
            rotation
          };
        })
      );
    } else {
      const defaultWatermarks = [
        {
          name: 'tradeSecretLabel',
          text:
            selectedProjectDocument?.accessControl.name.toUpperCase() ||
            t('projectDocumentVersionFileView.labels.scgConfidential').toUpperCase(),
          position:
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.excel
              ? { id: 1, name: 'Top-Left', value: 'TopLeft' }
              : { id: 3, name: 'Center', value: 'Center' },
          rotation:
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.excel
              ? { id: 1, name: '0', value: 0 }
              : { id: 2, name: '45', value: 45 },
          color: { id: 1, name: 'Red', value: '#EB4034' }
        },
        {
          name: 'actionByLabel',
          text: `${t('projectDocumentVersionFileView.labels.downloadBy')} ${
            user ? `${user.firstName} ${user.lastName}` : t('projectDocumentVersionFileView.labels.backspaceUser')
          }, ${moment().format('DD-MMM-YYYY')}`,
          position:
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.excel
              ? { id: 2, name: 'Top-Right', value: 'TopRight' }
              : { id: 3, name: 'Center', value: 'Center' },
          rotation:
            selectedProjectDocumentRevisionFile?.s3ContentType ===
            ContentTypeLabel.excel
              ? { id: 1, name: '0', value: 0 }
              : { id: 2, name: '45', value: 45 },
          color: { id: 1, name: 'Red', value: '#EB4034' }
        }
      ];
      setWatermarks(defaultWatermarks);
    }
  }, [selectedProjectDocument, selectedProjectDocumentRevisionFile]);

  useEffect(() => {
    dispatch(getProjectDocumentVersionFileAsync(versionId));
  }, []);

  function handleChangeValue(
    name: string,
    key: string,
    newValue: Position | Rotation | null
  ) {
    //Field mustn't empty because position field need other field to filter duplicate position
    if (newValue) {
      const watermarkId = watermarks.findIndex(
        (watermark) => watermark.name === name
      );
      let newWatermark = { ...watermarks[watermarkId], [key]: newValue };

      //Reset rotation to 0 degree if position isn't change to Center
      if (
        ((newValue.value === 'TopLeft' ||
          newValue.value === 'TopRight' ||
          newValue.value === 'BottomLeft' ||
          newValue.value === 'BottomRight') &&
          newWatermark.rotation.value === 45) ||
        (newValue.value === 'Center' && newWatermark.rotation.value === 90)
      ) {
        newWatermark = {
          ...newWatermark,
          rotation: { id: 1, name: '0', value: 0 }
        };
      }

      const newWatermarks = [
        ...watermarks.slice(0, watermarkId),
        newWatermark,
        ...watermarks.slice(watermarkId + 1)
      ];
      setWatermarks(newWatermarks);

      // Save watermark settings to local storage
      localStorage.setItem('watermark', JSON.stringify(newWatermarks));
    }
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      setLoading(true);
      if (selectedProjectDocumentRevisionFile) {
        const submittedWatermark: SubmittedWatermark = {
          positions: watermarks.map((watermark) => watermark.position.value),
          rotations: watermarks.map((watermark) => watermark.rotation.value),
          colors: watermarks.map((watermark) => watermark.color.value),
          actionDate: moment().toDate()
        };
        await dispatch(
          getProjectDocumentVersionFileOnlyAsync({
            projectDocumentVersionFileId: versionId,
            contentType: selectedProjectDocumentRevisionFile.s3ContentType,
            filename: selectedProjectDocumentRevisionFile.name,
            ...submittedWatermark
          })
        ).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('projectDocumentVersionFileView.messages.fileDownloadedSuccessfully') as string
          })
        );
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      }
    } catch (error) {
      if (error instanceof Error) {
        dispatch(
          loadingSlice.actions.error({
            message: error.message
          })
        );
      }
      setLoading(false);
    }
  }

  return (
    <div className={classes.root}>
      <Breadcrumb
        name="downloadFile"
        data={{
          projectName: selectedProject?.name,
          projectId: selectedProject?.id,
          documentName: selectedProjectDocument?.name,
          documentId: selectedProjectDocument?.id,
          revisionName: `Purpose: ${selectedProjectDocumentRevision?.purpose.abbreviation}, Revision: ${selectedProjectDocumentRevision?.revision.name}`,
          revisionId: selectedProjectDocumentRevision?.id,
          documentFileName: selectedProjectDocumentRevisionFile?.name,
          documentFileId: selectedProjectDocumentRevisionFile?.id
        }}
      />
      <Typography className={classes.title} component="p" variant="h4">
        {t('projectDocumentVersionFileView.title')}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3} className={classes.showInformation}>
          {selectedProject && <ProjectInfoCard project={selectedProject} />}
          {selectedProjectDocument && selectedProjectDocumentRevision && (
            <DocRevisionInfoCard
              document={selectedProjectDocument}
              docRevision={selectedProjectDocumentRevision}
            />
          )}
        </Grid>
        <Grid item xs={12} md={9}>
          <Paper className={classes.card}>
            <Typography variant="subtitle1" gutterBottom>
              {t('projectDocumentVersionFileView.sections.adjustWatermark')}
            </Typography>
            <Divider className={classes.divider} />
            <form onSubmit={onSubmit}>
              {watermarks.map((watermark, index) => {
                return (
                  <div key={watermark.name}>
                    <div
                      className={clsx(
                        classes.flexAlignCenter,
                        classes.marginBottom
                      )}
                    >
                      <Avatar className={classes.primary} variant="rounded">
                        A{index + 1}
                      </Avatar>
                      <Typography
                        className={classes.watermarkText}
                        style={{ color: watermark.color.value }}
                      >
                        {watermark.text}
                      </Typography>
                    </div>
                    <Grid className={classes.gridAxis} container spacing={1}>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          id="position"
                          //Filter duplicate position out from all position field
                          options={getPositions(t).filter((position: Position) =>
                            watermarks
                              .filter(
                                (watermarkEachItem) =>
                                  watermarkEachItem.name !== watermark.name
                              )
                              .every(
                                (watermarkEachItem) =>
                                  watermarkEachItem.position.value ===
                                    'Center' ||
                                  watermarkEachItem.position.id !== position.id
                              )
                          )}
                          getOptionLabel={(option) => option?.name}
                          getOptionSelected={(option, value) =>
                            option.id === value.id
                          }
                          value={watermark.position}
                          onChange={(event, newValue) =>
                            handleChangeValue(
                              watermark.name,
                              'position',
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label={t('projectDocumentVersionFileView.labels.position')}
                              required
                            />
                          )}
                          disabled={!isPdfFile}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          id="rotation"
                          //Filter rotation 45 degree out if position isn't Center and Filter rotation 90 degree out if position is Center
                          options={rotations.filter(
                            (rotation) =>
                              (watermark.position.value === 'Center' ||
                                rotation.value !== 45) &&
                              (watermark.position.value !== 'Center' ||
                                rotation.value !== 90)
                          )}
                          getOptionLabel={(option) => option?.name}
                          getOptionSelected={(option, value) =>
                            option.id === value.id
                          }
                          value={watermark.rotation}
                          onChange={(event, newValue) =>
                            handleChangeValue(
                              watermark.name,
                              'rotation',
                              newValue
                            )
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label={t('projectDocumentVersionFileView.labels.rotation')}
                              required
                            />
                          )}
                          disabled={!isPdfFile}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Autocomplete
                          id="color"
                          options={getColors(t)}
                          getOptionLabel={(option) => option?.name}
                          getOptionSelected={(option, value) =>
                            option.id === value.id
                          }
                          value={watermark.color}
                          onChange={(event, newValue) =>
                            handleChangeValue(watermark.name, 'color', newValue)
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              size="small"
                              label={t('projectDocumentVersionFileView.labels.color')}
                              required
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </div>
                );
              })}
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={<GetAppRounded />}
              >
                {t('projectDocumentVersionFileView.buttons.download')}
              </Button>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(1.5, 0, 12, 0)
    },
    title: {
      margin: theme.spacing(1.5, 0, 2, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(3.5, 0, 4, 0)
      }
    },
    card: {
      padding: theme.spacing(2),
      marginBottom: theme.spacing(2),
      borderRadius: theme.spacing(2)
    },
    paper: {
      padding: theme.spacing(1, 1.5),
      marginBottom: theme.spacing(1),
      borderRadius: theme.spacing(1),
      '&:last-child': {
        marginBottom: 0
      }
    },
    divider: {
      margin: theme.spacing(0.5, 0, 2, 0),
      width: theme.spacing(4)
    },
    flexAlignCenter: {
      display: 'flex',
      alignItems: 'center'
    },
    marginBottom: {
      marginBottom: theme.spacing(1)
    },
    showInformation: {
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    primary: {
      color: theme.palette.getContrastText(theme.palette.primary.main),
      backgroundColor: theme.palette.primary.main
    },
    watermarkText: {
      marginLeft: theme.spacing(1),
      lineHeight: '120%'
    },
    gridAxis: {
      padding: theme.spacing(0, 0, 2, 6)
    }
  })
);
